import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, PackageOpen } from 'lucide-react';
import { successToast } from 'hooks/use-toast';
import { LoadingOverlay } from 'components/core/loading-overlay';
import { WarningAlert } from 'components/blocks/warning-alert';
import ConfirmationModal from 'components/blocks/confirmation-modal/confirmation-modal';
import { SubscriptionPlan, ApiPlan } from 'features/subscription/components/subscription-plan';
import { useGetSubscriptionPlans, useGetCurrentSubscription, useCancelSubscription } from 'features/subscription/hooks/use-subscription';
import { useSubscriptionStore } from 'state/store/subscription';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { isIncompleteCheckout } from 'features/subscription/utils/resolve-plan-select-action';
import { visibleSubscriptionPlans } from 'features/subscription/constants/plan-display';
import { usePlanSeats } from 'features/subscription/hooks/use-seats';
import { defaultSeatInterval, planSeatsOfUser, withInterval } from 'features/subscription/utils/seat-pools';
import { AddSeatDialog } from 'features/iam/components/seats/add-seat-dialog';
import { useGivePlan } from 'features/iam/components/seats/use-give-plan';
import { cn } from 'lib/utils';

export const SubscriptionPlansPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const { data: subscriptionPlans, isLoading } = useGetSubscriptionPlans();
  useGetCurrentSubscription();
  const subscriptionDetails = useSubscriptionStore((s) => s.subscriptionDetails);
  const isOnlyOrgUser = useCommonStore((s) => s.isOnlyOrgUser);
  const { mutateAsync: cancelSubscription, isPending: isCancelling } = useCancelSubscription();
  const hasPendingPayment = isIncompleteCheckout(subscriptionDetails?.status);
  // Every plan is per seat, so choosing one moves your own seat to it, checked before any payment.
  const user = useCommonStore((s) => s.user);
  const { planSeats } = usePlanSeats();
  const { givePlan, isPending: isSwitching, dialog } = useGivePlan(planSeats);
  const myPlan = user?.id ? planSeatsOfUser(planSeats, user.id) : undefined;
  const switchTo = (plan: ApiPlan, interval: string) => {
    const entry = planSeats.find((candidate) => candidate.code === plan.code);
    if (!entry || !user?.id) return;
    const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email;
    const showSwitched = () =>
      successToast({
        title: t('SUCCESS'),
        description: t('PLAN_SWITCHED_TO', { plan: entry.name, defaultValue: 'You are now on the {{plan}} plan.' }),
      });
    givePlan([{ userId: user.id, name }], withInterval(entry, interval), undefined, showSwitched);
  };

  const visiblePlans: ApiPlan[] = visibleSubscriptionPlans(subscriptionPlans?.data ?? []);

  const handleConfirmCancel = async () => {
    const subscriptionId = subscriptionDetails?.subscriptionId;
    if (!subscriptionId) return;
    await cancelSubscription(subscriptionId);
  };

  return (
    <>
      <div className="min-h-screen bg-[#faf9f5] py-12 px-4 font-sans dark:bg-background sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-7xl flex-col items-center">
          <div className="w-full flex items-center justify-start mb-4">
            <button
              type="button"
              onClick={() => navigate('/chat')}
              className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-1.5 px-3 rounded-lg hover:bg-muted/80 cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4 shrink-0" />
              <span>{t('BACK', 'Back')}</span>
            </button>
          </div>

          {hasPendingPayment && (
            <WarningAlert className="mb-6 w-full max-w-3xl">
              {t(
                'PAYMENT_PENDING_WARNING_PREFIX',
                'You have a payment pending. Please complete the payment or'
              )}{' '}
              {isOnlyOrgUser ? (
                t('UNSUBSCRIBE', 'unsubscribe')
              ) : (
                <button
                  type="button"
                  className="inline p-0 font-semibold text-warning-high-emphasis underline underline-offset-2 hover:text-warning-high-emphasis/80 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => setIsCancelOpen(true)}
                  disabled={isCancelling}
                >
                  {t('UNSUBSCRIBE', 'unsubscribe')}
                </button>
              )}{' '}
              {t('PAYMENT_PENDING_WARNING_SUFFIX', 'to choose a different plan.')}
            </WarningAlert>
          )}

          <h1 className="mb-10 text-center font-sans text-3xl font-bold text-[#171b23] dark:text-foreground">
            {t('CHOOSE_YOUR_PLAN', 'Choose Your Plan')}
          </h1>

          <div
            className={cn(
              'grid w-full items-stretch gap-5 [grid-template-rows:auto_auto_auto_auto_1fr]',
              visiblePlans.length <= 1 && 'mx-auto max-w-[380px] grid-cols-1',
              visiblePlans.length === 2 && 'mx-auto max-w-3xl grid-cols-1 sm:grid-cols-2',
              visiblePlans.length >= 3 && 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3'
            )}
          >
            {isLoading ? (
              <div className="col-span-full">
                <LoadingOverlay />
              </div>
            ) : visiblePlans.length === 0 ? (
              <div className="col-span-full mx-auto flex w-full max-w-md flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-muted/30 px-6 py-16 text-center shadow-sm">
                <PackageOpen className="mb-4 h-12 w-12 text-muted-foreground/50" />
                <h3 className="mb-2 text-lg font-semibold text-foreground">
                  {t('NO_PLANS_TITLE', 'No Subscription Plans')}
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {t('NO_PLANS_DESC', 'There are currently no subscription plans available for this organization. Please contact your administrator.')}
                </p>
              </div>
            ) : (
              visiblePlans.map((plan: ApiPlan) => {
                const entry = planSeats.find((candidate) => candidate.code === plan.code);
                return (
                  <SubscriptionPlan
                    key={plan.planId}
                    plan={plan}
                    seat={{
                      isCurrent: myPlan?.code === plan.code,
                      busy: isSwitching,
                      pendingCheckout: entry?.awaitingPayment ? entry : undefined,
                      boughtInterval: entry?.pool ? defaultSeatInterval(entry) : undefined,
                      onSwitch: (interval) => switchTo(plan, interval),
                    }}
                  />
                );
              })
            )}
          </div>

        </div>
      </div>

      <AddSeatDialog {...dialog} />

      <ConfirmationModal
        open={isCancelOpen}
        onOpenChange={setIsCancelOpen}
        bgBlurred
        confirmVariant="delete"
        title={t('CANCEL_SUBSCRIPTION', 'Cancel Subscription')}
        description={
          <>
            {t('UNSUBSCRIBE_PLAN_CONFIRM_PREFIX', 'Are you sure to unsubscribe the')}{' '}
            <strong className="font-bold text-foreground">
              {subscriptionDetails?.planName || subscriptionDetails?.planCode || ''}
            </strong>
            {t('UNSUBSCRIBE_PLAN_CONFIRM_SUFFIX', '?')}
          </>
        }
        confirmText={t('CANCEL_SUBSCRIPTION', 'Cancel Subscription')}
        cancelText={t('KEEP_SUBSCRIPTION', 'Keep Subscription')}
        isLoading={isCancelling}
        onConfirm={handleConfirmCancel}
      />
    </>
  );
};

export default SubscriptionPlansPage;
